import { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import { ClaimStates } from "./ClaimStates";
import NavButtons from "../returns/NavButtons";
import "./ClaimsOrderSelection.css";
import { defaultTheme, useTheme, useFlowPortalContent } from "../../context/PortalThemeContext";
import { fallbackString } from "../shared/FallbackUtils";
import { getDiscountInfo } from "../../utils/discountUtils";
import { PiCheck } from "react-icons/pi";

const ClaimsOrderSelection = ({
  orderDetails,
  itemsToClaim,
  onStepChange,
  selectedItems = [],
  setSelectedItems,
  claimReason,
  shopifyProducts,
  flowType = 'CLAIM',
  claimFlow = null
}) => {
  const [error, setError] = useState(false);
  const theme = useTheme();
  const [claimQuantitys, setClaimQuantitys] = useState({});
  const [quantitiesAlreadyClaimed, setQuantitiesAlreadyClaimed] = useState({});
  const [isInClaimMap, setIsInClaimMap] = useState({});

  const allowMultiItemSelect = claimReason?.allowMultiItemSelect ?? false;

  // Use the new flow-specific hook
  const flowPortalContent = useFlowPortalContent(flowType);
  const itemSelectionSettings = flowPortalContent?.itemSelection;
  const defaultSettings = defaultTheme.flowPortalContentSettings.CLAIM.itemSelection;

  // Calculate total order value for proportional discounts
  const subtotalLineItems = orderDetails.line_items.reduce((sum, item) => sum + parseFloat(item.price), 0);

  const handleQuantityChange = (itemId, quantity) => {
    setClaimQuantitys(prev => ({
      ...prev,
      [itemId]: quantity
    }));

    // If changing after selected, update
    if (selectedItems.find(item => item.id === itemId)) {
      setSelectedItems(prev => prev.map(item =>
        item.id === itemId ? { ...item, claimQuantity: quantity } : item
      ));
    }
  }

  useEffect(() => {
    orderDetails.line_items.forEach((lineItem) => {
      const lineItemIdStr = String(lineItem.id);

      // Calculate quantity that has been claimed in PREVIOUS claims only
      let quantityClaimedPreviously = 0;

      if (Array.isArray(orderDetails.orderClaims)) {
        orderDetails.orderClaims.forEach((claim) => {
          // Only consider claims that are not in the initial review state
          if (claim.reviewStatus && claim.reviewStatus !== 'NOT_REVIEWED') {
            claim.claimedItems?.forEach((claimedItem) => {
              const claimedItemId = String(claimedItem.itemId || claimedItem.lineItemId);
              if (claimedItemId === lineItemIdStr) {
                quantityClaimedPreviously += (claimedItem.quantity || 1);
              }
            });
          }
        });
      }

      setQuantitiesAlreadyClaimed((prev) => ({
        ...prev,
        [lineItemIdStr]: quantityClaimedPreviously,
      }));

      // Mark as "in a claim" only if fully covered by previous claims, not current selection
      setIsInClaimMap((prev) => ({
        ...prev,
        [lineItemIdStr]: quantityClaimedPreviously === lineItem.quantity,
      }));
    });
  }, [orderDetails]);

  const toggleItemSelection = (item) => {
    const lineItemIdStr = String(item.id);

    if (selectedItems.find(selectedItem => selectedItem.id === item.id)) {
      setSelectedItems(prev => prev.filter(selectedItem => selectedItem.id !== item.id));
    } else {
      if (!allowMultiItemSelect) {
        setSelectedItems([{ ...item, claimQuantity: (claimQuantitys[item.id] || 1) }]);
      } else {
        setSelectedItems(prev => [...prev, { ...item, claimQuantity: (claimQuantitys[item.id] || 1) }]);
      }
    }
  }

  const handleBack = () => {
    setSelectedItems([]);
    onStepChange(ClaimStates.CHOOSE_CLAIM_REASON);
  }

  const handleNext = () => {
    onStepChange(ClaimStates.CHOOSE_ISSUE);
  };

  // Don't render if we don't have order details
  if (!orderDetails || !orderDetails.line_items) {
    return (
      <div className="claims-order-selection">
        <div className="text-center p-4">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading order details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="claims-order-selection">
      <div className="claims-order-header">
        <h2>{fallbackString(itemSelectionSettings?.headingText, defaultSettings.headingText)}</h2>
        <p>{fallbackString(itemSelectionSettings?.subheadingText, defaultSettings.subheadingText)}</p>
        {claimReason && (
          <div className="selected-reason-display">
            <strong>{fallbackString(itemSelectionSettings?.claimReasonLabel, defaultSettings.claimReasonLabel)}:</strong> {claimReason.name}
          </div>
        )}
      </div>

      <div className="claim-items-list">
        {orderDetails?.line_items?.map((lineItem) => {
          if (!lineItem || !lineItem.id) return null;

          const lineItemIdStr = String(lineItem.id);
          const quantityAlreadyClaimed = quantitiesAlreadyClaimed[lineItemIdStr] || 0;
          const isInClaim = isInClaimMap[lineItemIdStr] || false;
          const isSelected = selectedItems?.find(item => item?.id === lineItem.id);
          const availableQuantity = (lineItem.quantity || 0) - quantityAlreadyClaimed;
          const currentClaimQuantity = claimQuantitys[lineItem.id] || 1;

          const isEligibleForClaim = lineItem.eligibleForClaim === true && !isInClaim;
          let reasonForClaimIneligibility = null;

          if (!isEligibleForClaim) {
            if (lineItem?.reasonForClaimIneligibility) {
              reasonForClaimIneligibility = lineItem.reasonForClaimIneligibility;
            } else if (isInClaim) {
              reasonForClaimIneligibility = "Item already in claim";
            } else {
              reasonForClaimIneligibility = "Not available for claim";
            }
          }

          if (availableQuantity <= 0) return null;

          let discountInfo = { hasDiscount: false, discountedPrice: parseFloat(lineItem.price || 0), shouldShowCrossedOut: false };
          let discountedPrice = parseFloat(lineItem.price || 0);

          try {
            discountInfo = getDiscountInfo(lineItem);
            discountedPrice = discountInfo.discountedPrice;
          } catch (err) {
            discountedPrice = parseFloat(lineItem.price || 0);
          }

          return (
            <button
              key={lineItem.id}
              disabled={!isEligibleForClaim}
              className={`claim-item-card ${isSelected ? 'selected' : ''} ${!isEligibleForClaim ? 'disabled' : ''}`}
              onClick={() => isEligibleForClaim && toggleItemSelection(lineItem)}
            >
              <div className="claim-item-thumb">
                {lineItem.image || lineItem.image_src ? (
                  <img src={lineItem.image || lineItem.image_src} alt={lineItem.name} />
                ) : (
                  <div className="claim-item-thumb-placeholder">IMG</div>
                )}
              </div>

              <div className="claim-item-info">
                <p className="claim-item-name">{lineItem.name}</p>
                <p className="claim-item-price">
                  {discountInfo.shouldShowCrossedOut && (
                    <span className="claim-item-price-old">${lineItem.price}</span>
                  )}
                  ${discountedPrice.toFixed(2)}
                </p>
                {lineItem.variant_title && (
                  <p className="claim-item-variant">{lineItem.variant_title}</p>
                )}
                {!isEligibleForClaim && (
                  <p className="claim-item-ineligible">{reasonForClaimIneligibility}</p>
                )}
              </div>

              <div className="claim-item-end">
                {isEligibleForClaim && (
                  <>
                    <span className="claim-item-qty">
                      {availableQuantity > 1 ? (
                        <Form.Select
                          value={currentClaimQuantity}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => handleQuantityChange(lineItem.id, parseInt(e.target.value))}
                          className="claim-item-qty-select"
                        >
                          {Array.from({ length: availableQuantity }, (_, i) => i + 1).map((q) => (
                            <option key={q} value={q}>{q}</option>
                          ))}
                        </Form.Select>
                      ) : (
                        `Qty: ${availableQuantity}`
                      )}
                    </span>
                    {isSelected && (
                      <span className="claim-item-badge">
                        <PiCheck size={12} /> Selected
                      </span>
                    )}
                  </>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {error && (
        <div className="alert alert-danger">
          {fallbackString(itemSelectionSettings?.errorMessage, defaultSettings.errorMessage)}
        </div>
      )}

      <NavButtons
        handleBack={handleBack}
        handleNext={handleNext}
        disabled={!selectedItems.length}
        onDisabledClick={() => setError(true)}
      />
    </div>
  );
};

export default ClaimsOrderSelection;
